/**
 * Custom Outgoing Hook
 * Demonstrates pipeline extensibility for outgoing messages
 * Example: Apply transformations, compression, signing, encryption
 */
import { BaseHook, type IPipelineContext, type HookResult } from 'edge-stream-js';
import { type HookMetadata } from '../HookMetadata';

export class CustomOutgoingHook extends BaseHook {
  override name = 'CustomOutgoingHook';
  override priority = 75; // Executes after ValidationHook (20) and FilterHook (50)

  metadata?: HookMetadata = {
    name: 'CustomOutgoingHook',
    description: 'Applies transformations to outgoing messages including digital signature, compression metadata, message classification (question/emphasis/statement), and complexity analysis',
    version: '1.0.0',
    author: 'BizFirstGO',
    tags: ['transformation', 'outgoing', 'security', 'compression', 'classification'],
    modifiesData: true,
    impactLevel: 'high',
  };

  async execute(context: IPipelineContext): Promise<HookResult> {
    const messageId = context.envelope.meta.id;
    const body = context.envelope.body as any;
    const messageData = body?.data?.data || {};

    console.log(`[CustomOutgoingHook] 🚀 Processing custom outgoing logic for: ${messageId}`);

    // Example 1: Apply transformations
    messageData.transformedAt = new Date().toISOString();
    messageData.clientVersion = navigator?.userAgent?.substring(0, 50) || 'unknown';

    // Example 2: Add digital signature (simulated)
    messageData.signature = this.generateSimpleHash(messageData.text);

    // Example 3: Prepare for transmission
    messageData.encoding = 'utf-8';
    messageData.encrypted = false; // Would be true in production

    // Example 4: Message classification
    const textLength = (messageData.text || '').length;
    messageData.classification = {
      type: this.classifyMessage(messageData.text || ''),
      complexity: this.calculateComplexity(messageData.text || ''),
      priority: textLength > 500 ? 'high' : 'normal',
    };

    // Example 5: Add compression metadata
    messageData.compression = {
      originalSize: JSON.stringify(messageData).length,
      algorithm: 'gzip',
      enabled: true,
    };

    console.log(`[CustomOutgoingHook] ✅ Outgoing message transformed: ${messageId}`, {
      signature: messageData.signature,
      classification: messageData.classification,
      compressed: messageData.compression.enabled,
    });

    // Must return hook result for pipeline to continue
    return this.continue();
  }

  private generateSimpleHash(text: string): string {
    // Simulated hash function (not cryptographically secure)
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  }

  private classifyMessage(text: string): string {
    if (text.includes('?')) return 'question';
    if (text.includes('!')) return 'emphasis';
    if (text.includes(':')) return 'statement';
    return 'general';
  }

  private calculateComplexity(text: string): 'low' | 'medium' | 'high' {
    const wordCount = text.split(/\s+/).length;
    const avgWordLength = text.length / wordCount;

    if (wordCount < 5 && avgWordLength < 5) return 'low';
    if (wordCount < 20 && avgWordLength < 8) return 'medium';
    return 'high';
  }
}
